<template>
  <v-btn
    variant="text"
    small
    class="logoutButton"
    @click="changeSubject"
  >
    {{ $t('profile.changeSubject') }} &nbsp;
    <v-icon>mdi-cached</v-icon>
  </v-btn>
  <v-tooltip interactive>
    <template #activator="{ props: activatorProps }">
      <v-btn
        variant="text"
        small
        class="logoutButton"
        v-bind="activatorProps"
        @click="logout"
      >
        {{ $t('profile.logout') }} &nbsp;
        <v-icon>mdi-location-exit</v-icon>
      </v-btn>
    </template>
    <div>
      {{ $t('profile.sessionExpiredAt') }} {{ countdownText }}
      <button
        class="renew-btn"
        @click="renewToken"
      >
        <v-icon>mdi-refresh</v-icon>
      </button>
    </div>
  </v-tooltip>
</template>

<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import keycloak, { logoutFromApp } from '@/plugins/keycloak';
  import router from '@/router';
  import { useInteractionModeStore } from '@/store/interactionModeStore';

  const now = ref(Date.now());
  let timer: number | undefined;
  const interactionModeStore = useInteractionModeStore();

  const countdownText = computed(() => {
    const expiration = keycloak?.getSessionExpiration();
    if (!expiration) return 'N/A';

    const diff = expiration.getTime() - now.value;
    if (diff <= 0) return 'Expired';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    const pad = (n: number) => (n < 10 ? '0' : '') + n;

    return `${pad(minutes)}:${pad(seconds)}`;
  });

  async function renewToken() {
    try {
      // Passing -1 or a low value forces a refresh if the refresh token is valid
      await keycloak.updateToken(-1);
      now.value = Date.now(); // Force UI update
    } catch (error) {
      console.error('Failed to renew token', error);
    }
  }

  function logout() {
    interactionModeStore.leaveEditMode();
    logoutFromApp();
  }

  function changeSubject() {
    localStorage.removeItem('selectedSubjectIdentifier');
    router.push('/landing');
  }

  function isSessionExpired() {
    const expiration = keycloak?.getSessionExpiration();
    if (!expiration) return true;

    const diff = expiration.getTime() - now.value;
    return diff <= 0;
  }

  onMounted(() => {
    timer = window.setInterval(() => {
      now.value = Date.now();
      if (isSessionExpired()) {
        logout();
      }
    }, 1000);
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
  });
</script>

<style scoped lang="scss">
  .logoutButton {
    color: $grey300Color !important;
  }
</style>
